import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MatriculaAlunoSchema } from '@/schema/matriculaAlunoSchema';
import { Select, Divider, Row, Col, Button, DatePickerProps, ConfigProvider, DatePicker } from "antd";
import { brazilStates, booleanState } from '@/utils/staticData';
import { getEnderecoByCep } from '@/services/enderecoByCep';
import { getByCpf } from '@/services/pessoa';
import { Input } from '@/components/ui/inputForm';
import { API_CONSTANT_MAP } from '@/services/paths';
import { FormSearchInput } from '@/components/Form/FormSearchInput';
import TextArea from 'antd/es/input/TextArea';
import { MaskedInput, createDefaultMaskGenerator } from 'react-hook-mask';
import { cn } from '@/lib/utils';
import locale from 'antd/locale/pt_BR';
import dayjs from 'dayjs';

import 'dayjs/locale/pt-br';
import { Container } from '../styles';
import { useUpdateEffect } from '@/hooks/useUpdateEffect';
import { set } from 'lodash';
import { CopyOutlined } from '@ant-design/icons';
import { parseAnyToDayjs, formatToBr } from '@/utils/dateUtils';
import cpfValidation from '@/utils/cpfValidation';
import { errorHandler } from '@/utils/errorhandler';
import { preencherPessoaNoForm } from '../preencherPessoa';

dayjs.locale('pt-br');
const dateFormat = 'DD/MM/YYYY';

const maskCelular = createDefaultMaskGenerator('(99) 99999-9999');
const maskTelefone = createDefaultMaskGenerator('(99) 9999-9999');
const maskCPF = createDefaultMaskGenerator('999.999.999-99');
const maskData = createDefaultMaskGenerator('99/99/9999');

type Props = {
  current: number;
};

const Student: React.FC<Props> = ({ current }) => {

  const [blockedFields, setBlockedFields] = useState(false);
  // A consulta automática por CPF só pode rodar quando o CPF foi de fato
  // digitado. Ao remontar o passo (ex.: voltar de Específicas), o MaskedInput
  // dispara um onChange de sincronização com o CPF já carregado; sem esta
  // guarda a consulta rodava sozinha e o `preencherEstudante` sobrescrevia com
  // os dados do banco o e-mail/celular/telefone recém-preenchidos na tela.
  // Mesma proteção usada no passo Responsável.
  const cpfUserInteraction = useRef(false);
  const ultimoCpfConsultado = useRef<string | null>(null);
  const [notEnsinoMedio, setNotEnsinoMedio] = useState<boolean>();
  const [hiddenOutroTipoIndicacao, setHiddenOutroTipoIndicacao] = useState<boolean>(true);
  const [cpfEstudante, setCpfEstudante] = useState<string>('');
  const [celularEtudante, setCelularEstudante] = useState<string>('');
  const [telefoneEstudante, setTelefoneEstudante] = useState<string|undefined>('');
  const [dataNascimentoEstudante, setDataNascimentoEstudante] = useState<dayjs.Dayjs | null>(null);

  const {
    register,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors: { estudante: estudanteErrors } },
    control
  } = useFormContext<MatriculaAlunoSchema>();

  const { Option } = Select;

  const values = getValues();

  useEffect(() => {
    let ensinoMedio = getValues("estudante.alunoEM");
    if (ensinoMedio) {
      setNotEnsinoMedio(ensinoMedio != '1');
    }
    setCpfEstudante(getValues("estudante.pessoaEstudante.cpfCnpj") || '');
    setCelularEstudante(getValues("estudante.pessoaEstudante.celular") || '');
    setTelefoneEstudante(getValues("estudante.pessoaEstudante.telefone") || '');
    
    // Inicializar data de nascimento se já existir no formulário
    const dataExistente = getValues("estudante.pessoaEstudante.dataNascimento");
    if (dataExistente) {
      setDataNascimentoEstudante(parseAnyToDayjs(dataExistente));
    }
  }, [values, estudanteErrors]);

  function verifyErrorRgState(): any {
    if (estudanteErrors?.pessoaEstudante?.ufRg) return 'O Estado de Emissão é obrigatório';
    return null;
  }


  function verifyErrorAddressState(): string | null {
    if (estudanteErrors?.pessoaEstudante?.uf) return 'O Estado é obrigatório';
    return null;
  }

  const getBuscaCEP = async (e: { target: { value: string } }) => {
    const cep = e.target.value.replace(/\D/g, '');
    if (!cep) {
      return;
    }
    const data: any = await getEnderecoByCep(cep);
    if (!data?.data?.cep) {
      return;
    }
    setValue("estudante.pessoaEstudante.cep", data.data.cep);
    setValue("estudante.pessoaEstudante.uf", data.data.uf);
    setValue("estudante.pessoaEstudante.cidade", data.data.localidade);
    setValue("estudante.pessoaEstudante.bairro", data.data.bairro);
    setValue("estudante.pessoaEstudante.logradouro", data.data.logradouro);
  };

  const changeEnsinoMedio = async (e: string) => {
    const isEnsinoMedio = e === '1';
    setNotEnsinoMedio(!isEnsinoMedio);
    if (!isEnsinoMedio) {
      setValue("estudante.escolaEM", 0);
    } else {
      setValue("estudante.semestresEstudo", "0");
    }
  };

  const changeTipoIndicacao = async (e: string) => {
    const isOutroTipoIndicacao = e == '7';
    setHiddenOutroTipoIndicacao(!isOutroTipoIndicacao);
    if (!isOutroTipoIndicacao) {
      setValue("estudante.ondeConheceuAcademiaOutros", "");
    }
  };

  const copyResponsavelData = () => {
    setValue("estudante.pessoaEstudante.nome", getValues("responsavel.pessoaResponsavel.nome"));
    setValue("estudante.pessoaEstudante.cpfCnpj", getValues("responsavel.pessoaResponsavel.cpfCnpj"));
    setValue("estudante.pessoaEstudante.rg", getValues("responsavel.pessoaResponsavel.rg"));
    setValue("estudante.pessoaEstudante.orgaoEmissorRg", getValues("responsavel.pessoaResponsavel.orgaoEmissorRg"));
    setValue("estudante.pessoaEstudante.ufRg", getValues("responsavel.pessoaResponsavel.ufRg"));
    setValue("estudante.pessoaEstudante.email", getValues("responsavel.pessoaResponsavel.email"));
    setValue("estudante.pessoaEstudante.cep", getValues("responsavel.pessoaResponsavel.cep"));
    setValue("estudante.pessoaEstudante.uf", getValues("responsavel.pessoaResponsavel.uf"));
    setValue("estudante.pessoaEstudante.cidade", getValues("responsavel.pessoaResponsavel.cidade"));
    setValue("estudante.pessoaEstudante.bairro", getValues("responsavel.pessoaResponsavel.bairro"));
    setValue("estudante.pessoaEstudante.logradouro", getValues("responsavel.pessoaResponsavel.logradouro"));
    setValue("estudante.pessoaEstudante.numero", getValues("responsavel.pessoaResponsavel.numero"));
    setValue("estudante.pessoaEstudante.complemento", getValues("responsavel.pessoaResponsavel.complemento"));
    setValue("estudante.pessoaEstudante.telefone", getValues("responsavel.pessoaResponsavel.telefone"));
    setValue("estudante.pessoaEstudante.celular", getValues("responsavel.pessoaResponsavel.celular"));
    setValue("estudante.pessoaEstudante.dataNascimento", getValues("responsavel.pessoaResponsavel.dataNascimento"));
    setCpfEstudante(getValues("responsavel.pessoaResponsavel.cpfCnpj") || '');
    setCelularEstudante(getValues("responsavel.pessoaResponsavel.celular") || '');
    setTelefoneEstudante(getValues("responsavel.pessoaResponsavel.telefone") || '');
    setDataNascimentoEstudante(
      parseAnyToDayjs(getValues("responsavel.pessoaResponsavel.dataNascimento"))
    );
  }

  const copyResponsavelAddressData = () => {
    setValue("estudante.pessoaEstudante.cep", getValues("responsavel.pessoaResponsavel.cep"));
    setValue("estudante.pessoaEstudante.uf", getValues("responsavel.pessoaResponsavel.uf"));
    setValue("estudante.pessoaEstudante.cidade", getValues("responsavel.pessoaResponsavel.cidade"));
    setValue("estudante.pessoaEstudante.bairro", getValues("responsavel.pessoaResponsavel.bairro"));
    setValue("estudante.pessoaEstudante.logradouro", getValues("responsavel.pessoaResponsavel.logradouro"));
    setValue("estudante.pessoaEstudante.numero", getValues("responsavel.pessoaResponsavel.numero"));
    setValue("estudante.pessoaEstudante.complemento", getValues("responsavel.pessoaResponsavel.complemento"));
  }

  const SelectUF: React.FC<{ registerPath: any, blocked: boolean, errorMessage: string | null }> = ({ registerPath, blocked, errorMessage }) => {
    return <>
      <Controller
        control={control}
        name={registerPath}
        render={({ field }) => {
          return (
            <div>
              <Select
                {...field}
                allowClear
                showSearch
                disabled={blocked}
                placeholder="Selecione o Estado"
                style={{ width: '100%', height: '2.5rem' }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {brazilStates.map((item: any) => (
                  <Option
                    key={item?.label}
                    value={item?.label}
                  >
                    {item?.value}
                  </Option>
                ))}
              </Select>
            </div>
          );
        }} />
      {errorMessage && <span className='text-red-500 text-sm'>{errorMessage}</span>}
    </>
  };

  function setCelularValue(celular: any){
    setValue('estudante.pessoaEstudante.celular', celular);
    setCelularEstudante(celular);
  }

  function setTelefoneValue(telefone: any){
    setValue('estudante.pessoaEstudante.telefone', telefone);
    setTelefoneEstudante(telefone);
  }

  function setCpfValue(cpf: any) {
    setValue('estudante.pessoaEstudante.cpfCnpj', cpf);
    setCpfEstudante(cpf);
    
    const cpfDigits = (cpf || '').replace(/\D/g, '');
    if (cpfDigits.length === 0) {
      clearErrors('estudante.pessoaEstudante.cpfCnpj');
      return;
    }
    if (cpfDigits.length === 11) {
      if (!cpfValidation(cpfDigits)) {
        setError('estudante.pessoaEstudante.cpfCnpj', { type: 'manual', message: 'CPF inválido' });
        return;
      }
      clearErrors('estudante.pessoaEstudante.cpfCnpj');
      if (cpfUserInteraction.current) {
        consultarPessoaPorCpf(cpf);
      }
    } else {
      clearErrors('estudante.pessoaEstudante.cpfCnpj');
    }
  }

  function setDataNascimentoValue(dataNascimento: dayjs.Dayjs | null){
    setDataNascimentoEstudante(dataNascimento);
    setValue('estudante.pessoaEstudante.dataNascimento', formatToBr(dataNascimento));
  }

  // Função para consultar pessoa por CPF
  const consultarPessoaPorCpf = async (cpf: string) => {
    // Limpar máscara do CPF
    const cpfLimpo = cpf.replace(/\D/g, '');

    // Verificar se o CPF tem 11 dígitos
    if (cpfLimpo.length !== 11) {
      return;
    }

    // O MaskedInput dispara onChange a cada tecla: sem isto, corrigir um dígito
    // de um CPF já completo refazia a consulta e reaplicava o preenchimento.
    if (ultimoCpfConsultado.current === cpfLimpo) {
      return;
    }
    ultimoCpfConsultado.current = cpfLimpo;

    try {
      const alunoResp = await getByCpf(cpfLimpo, 1);
      if (alunoResp?.status === 200 && alunoResp.data) {
        const aluno = alunoResp.data;
        preencherEstudante(aluno?.aluno);
        preencherResponsavel(aluno?.responsavel);
      }
    } catch (error: any) {
      // CPF sem registro na base é caso normal, não erro: a tela fica exatamente
      // como está e a Pessoa é gravada no submit. Só falha de verdade avisa.
      const status = error?.response?.status;
      if (status === 404 || status === 204) {
        return;
      }
      errorHandler(error);
    }
  };

  const preencherEstudante = (pessoa: any) => {
    if (!pessoa) return;

    // O CPF fica de fora: é o que o usuário acabou de digitar nesta tela.
    const { dataNascimento, aplicados } = preencherPessoaNoForm(
      setValue,
      'estudante.pessoaEstudante',
      pessoa,
      { ignorar: ['cpfCnpj'] },
    );

    if (aplicados.celular) setCelularEstudante(aplicados.celular);
    if (aplicados.telefone) setTelefoneEstudante(aplicados.telefone);
    if (dataNascimento) setDataNascimentoEstudante(dataNascimento);
  };

  const preencherResponsavel = (pessoa: any) => {
    if (!pessoa) return;
    preencherPessoaNoForm(setValue, 'responsavel.pessoaResponsavel', pessoa);
  };

  return (
    <>
      <Container>
        <Row gutter={[16, 24]}>
          <Col md={24} lg={6}>
            <label>CPF</label>
            <MaskedInput
              maskGenerator={maskCPF}
              value={cpfEstudante}
              onChange={setCpfValue}
              onFocus={() => { cpfUserInteraction.current = true; }}
              onBlur={() => { cpfUserInteraction.current = false; }}
            />
            {estudanteErrors?.pessoaEstudante?.cpfCnpj && <span className='text-red-500 text-sm'>{estudanteErrors.pessoaEstudante.cpfCnpj.message}</span>}
          </Col>
          <Col md={24} lg={18}>
            <label>Nome</label>
            <Input
              type="text"
              disabled={blockedFields}
              {...register('estudante.pessoaEstudante.nome')}
            />
            {estudanteErrors?.pessoaEstudante?.nome && <span className='text-red-500 text-sm'>{estudanteErrors.pessoaEstudante.nome.message}</span>}
          </Col>
        </Row>
        <Divider orientation="left">RG</Divider>
        <Row gutter={[16, 24]}>
          <Col md={24} lg={8}>
            <label>Número</label>
            <Input
              type="number"
              disabled={blockedFields}
              placeholder='Somente números'
              {...register('estudante.pessoaEstudante.rg')}
            />
            {estudanteErrors?.pessoaEstudante?.rg && <span className='text-red-500 text-sm'>{estudanteErrors.pessoaEstudante.rg.message}</span>}
          </Col>
          <Col md={24} lg={8}>
            <label>Emissor</label>
            <Input
              type="text"
              disabled={blockedFields}
              {...register('estudante.pessoaEstudante.orgaoEmissorRg')}
            />
            {estudanteErrors?.pessoaEstudante?.orgaoEmissorRg && <span className='text-red-500 text-sm'>{estudanteErrors.pessoaEstudante.orgaoEmissorRg.message}</span>}
          </Col>
          <Col md={24} lg={8}>
            <label>Estado de Emissão</label>
            <SelectUF
              registerPath="estudante.pessoaEstudante.ufRg"
              blocked={blockedFields}
              errorMessage={verifyErrorRgState()}
            />
          </Col>
        </Row>
        <Divider orientation="left">Endereço</Divider>
        <Row gutter={[16, 24]} align="bottom">
          <Col md={24} lg={5}>
            <label>CEP</label>
            <Input
              type="text"
              disabled={blockedFields}
              placeholder='Somente números'
              {...register('estudante.pessoaEstudante.cep')}
              onBlur={getBuscaCEP}
            />
            {estudanteErrors?.pessoaEstudante?.cep && <span className='text-red-500 text-sm'>{estudanteErrors.pessoaEstudante.cep.message}</span>}
          </Col>
          <Col md={24} lg={2}>
            <label className="opacity-0 select-none">Copiar</label>
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={copyResponsavelAddressData}
              title="Copiar endereço do responsável"
              style={{ width: '100%', height: '2.5rem' }}
            />
          </Col>
          <Col md={24} lg={6}>
            <label>Estado</label>
            <SelectUF
              registerPath="estudante.pessoaEstudante.uf"
              blocked={blockedFields}
              errorMessage={verifyErrorAddressState()}
            />
          </Col>
          <Col md={24} lg={11}>
            <label>Cidade</label>
            <Input
              type="text"
              disabled={blockedFields}
              {...register('estudante.pessoaEstudante.cidade')}
            />
            {estudanteErrors?.pessoaEstudante?.cidade && <span className='text-red-500 text-sm'>{estudanteErrors.pessoaEstudante.cidade.message}</span>}
          </Col>
        </Row>
        <Row gutter={[16, 24]}>
          <Col md={24} lg={8}>
            <label>Bairro</label>
            <Input
              type="text"
              disabled={blockedFields}
              {...register('estudante.pessoaEstudante.bairro')}
            />
            {estudanteErrors?.pessoaEstudante?.bairro && <span className='text-red-500 text-sm'>{estudanteErrors.pessoaEstudante.bairro.message}</span>}
          </Col>
          <Col md={24} lg={12}>
            <label>Logradouro</label>
            <Input
              type="text"
              disabled={blockedFields}
              {...register('estudante.pessoaEstudante.logradouro')}
            />
            {estudanteErrors?.pessoaEstudante?.logradouro && <span className='text-red-500 text-sm'>{estudanteErrors.pessoaEstudante.logradouro.message}</span>}
          </Col>
          <Col md={24} lg={4}>
            <label>Número</label>
            <Input
              type="number"
              disabled={blockedFields}
              {...register('estudante.pessoaEstudante.numero')}
            />
            {estudanteErrors?.pessoaEstudante?.numero && <span className='text-red-500 text-sm'>{estudanteErrors.pessoaEstudante.numero.message}</span>}
          </Col>
        </Row>
        <Row gutter={[16, 24]}>
          <Col md={24}>
            <label>Complemento</label>
            <Input
              type="text"
              disabled={blockedFields}
              {...register('estudante.pessoaEstudante.complemento')}
            />
            {estudanteErrors?.pessoaEstudante?.complemento && <span className='text-red-500 text-sm'>{estudanteErrors.pessoaEstudante.complemento.message}</span>}
          </Col>
        </Row>
        <Divider orientation="left">Contato</Divider>
        <Row gutter={[16, 24]}>
          <Col md={8}>
            <label>E-mail</label>
            <Input
              type="email"
              disabled={blockedFields}
              {...register('estudante.pessoaEstudante.email')}
            />
            {estudanteErrors?.pessoaEstudante?.email && <span className='text-red-500 text-sm'>{estudanteErrors.pessoaEstudante.email.message}</span>}
          </Col>
          <Col md={24} lg={8}>
            <label>Celular</label>
            <MaskedInput
              maskGenerator={maskCelular}
              value={values?.estudante?.pessoaEstudante?.celular ?? ''}
              onChange={setCelularValue}
              placeholder='(99) 99999-9999'
            />
            {estudanteErrors?.pessoaEstudante?.celular && <span className='text-red-500 text-sm'>{estudanteErrors.pessoaEstudante.celular.message}</span>}
          </Col>
          <Col md={24} lg={8}>
            <label>Telefone</label>
            <MaskedInput
              maskGenerator={maskTelefone}
              value={values?.estudante?.pessoaEstudante?.telefone ?? ''}
              onChange={setTelefoneValue}
              placeholder='(99) 9999-9999'
            />
            {estudanteErrors?.pessoaEstudante?.telefone && <span className='text-red-500 text-sm'>{estudanteErrors.pessoaEstudante.telefone.message}</span>}
          </Col>
          <Col md={24} lg={8}>
            <label>Data Nascimento</label>
            <DatePicker
              value={dataNascimentoEstudante}
              onChange={setDataNascimentoValue}
              format="DD/MM/YYYY"
              placeholder="DD/MM/AAAA"
              disabled={blockedFields}
              style={{ width: '100%', height: '2.5rem' }}
            />
            {estudanteErrors?.pessoaEstudante?.dataNascimento && <span className='text-red-500 text-sm'>{estudanteErrors.pessoaEstudante.dataNascimento.message}</span>}
          </Col>
        </Row>
        <Divider orientation="left">Informações Adicionais</Divider>
        <Row gutter={[16, 24]}>
          <Col md={24} lg={6}>
            <FormSearchInput
              id="ensinoMedio"
              name="estudante.alunoEM"
              property="label"
              optionStatic={booleanState}
              inputValueName="label"
              valueProperty="value"
              label="É Aluno Ensino Médio?"
              onChangeCallback={changeEnsinoMedio}
            />
            {estudanteErrors?.alunoEM && <span className='text-red-500 text-sm'>{estudanteErrors.alunoEM.message}</span>}
          </Col>
          <Col md={24} lg={6} hidden={notEnsinoMedio}>
            <FormSearchInput
              id="escolaEM"
              name="estudante.escolaEM"
              property="nome"
              optionUrl={API_CONSTANT_MAP.escolaEnsinoMedio}
              inputValueName="nome"
              valueProperty="id"
              label="Escola Ensino Médio"
              sortOptions={(a: any, b: any) => {
                const aIsOutra = String(a?.label ?? '').trim().toUpperCase() === 'OUTRA';
                const bIsOutra = String(b?.label ?? '').trim().toUpperCase() === 'OUTRA';
                if (aIsOutra && !bIsOutra) return 1;
                if (!aIsOutra && bIsOutra) return -1;
                return String(a?.label ?? '').localeCompare(String(b?.label ?? ''), 'pt-BR');
              }}
            />
            {estudanteErrors?.escolaEM && <span className='text-red-500 text-sm'>{estudanteErrors.escolaEM.message}</span>}
          </Col>
          <Col md={24} lg={6} hidden={!notEnsinoMedio}>
            <label>Qtd. Semestre de Estudo</label>
            <Input
              type="number"
              disabled={blockedFields}
              placeholder='Quantidade de Semestres'
              {...register('estudante.semestresEstudo')}
            />
            {estudanteErrors?.semestresEstudo && <span className='text-red-500 text-sm'>{estudanteErrors.semestresEstudo.message}</span>}
          </Col>
          <Col md={24} lg={6}>
            <FormSearchInput
              id="ondeConheceuAcademia"
              name="estudante.ondeConheceuAcademia"
              property="nome"
              optionUrl={API_CONSTANT_MAP.tipoIndicacao}
              inputValueName="nome"
              valueProperty="id"
              label="De Onde Conhece Academia"
              onChangeCallback={changeTipoIndicacao}
            />
            {estudanteErrors?.ondeConheceuAcademia && <span className='text-red-500 text-sm'>{estudanteErrors.ondeConheceuAcademia.message}</span>}
          </Col>
          <Col md={24} lg={6} hidden={hiddenOutroTipoIndicacao}>
            <label>Outro:</label>
            <Input
              type="text"
              disabled={blockedFields}
              placeholder=''
              {...register('estudante.ondeConheceuAcademiaOutros')}
            />
            {estudanteErrors?.ondeConheceuAcademiaOutros && <span className='text-red-500 text-sm'>{estudanteErrors.ondeConheceuAcademiaOutros.message}</span>}
          </Col>
        </Row>
        <Row gutter={[16, 24]}>
          <Col md={24} lg={6}>
            <label>Curso Pretendido:</label>
            <Input
              type="text"
              disabled={blockedFields}
              placeholder=''
              {...register('estudante.cursoPretendido')}
            />
            {estudanteErrors?.cursoPretendido && <span className='text-red-500 text-sm'>{estudanteErrors.cursoPretendido.message}</span>}
          </Col>
        </Row>
        <Row>
          <Col md={24}>
            <label>Observação</label>
            <Controller
              name="estudante.observacao"
              control={control}
              render={({ field }) => (
                <TextArea
                  {...field}
                  rows={4}
                  disabled={blockedFields}
                  value={field.value || ''}
                  style={{ height: '5rem' }}
                />
              )}
            />
            {estudanteErrors?.observacao && <span className='text-red-500 text-sm'>{estudanteErrors.observacao.message}</span>}
          </Col>
        </Row>
        <Row gutter={[16, 24]}>
          <Col md={24} lg={6}>
            <Button type='primary' onClick={copyResponsavelData}>Copiar Dados Responsável</Button>
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default Student;